import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import api from '../../lib/api';
import { Globe, Search, Save, Clock, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminSiteSettingsPage = () => {
  const [settings, setSettings] = useState({
    site_name: '', site_url: '',
    meta_title: '', meta_description: '',
    logo_url: '', favicon_url: '',
    footer_text: '',
    social_facebook: '', social_twitter: '', social_instagram: '', social_linkedin: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Log retention state
  const [retention, setRetention] = useState({ user_activity_days: 90, admin_security_days: 180 });
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [retentionSaving, setRetentionSaving] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const res = await adminAPI.getSiteSettings();
        const s = res.data.settings || {};
        setSettings(prev => ({...prev, ...s}));
      } catch { toast.error('Failed to load settings'); }
      finally { setLoading(false); }

      try {
        const res = await api.get('/admin/security/log-retention');
        if (res.data.settings) setRetention(res.data.settings);
      } catch { /* silent */ }
      finally { setRetentionLoading(false); }
    };
    fetchAll();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSiteSettings(settings);
      toast.success('Site settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleRetentionSave = async () => {
    if (retention.user_activity_days < 7) {
      toast.error('Minimum 7 days for user activity logs');
      return;
    }
    if (retention.admin_security_days < 30) {
      toast.error('Minimum 30 days for security logs');
      return;
    }
    setRetentionSaving(true);
    try {
      await api.put('/admin/security/log-retention', retention);
      toast.success('Log retention settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save retention settings');
    } finally { setRetentionSaving(false); }
  };

  const handleRunCleanup = async () => {
    setCleanupRunning(true);
    try {
      const res = await api.post('/admin/security/log-cleanup');
      const d = res.data;
      toast.success(`Cleanup complete: ${d.user_logs_deleted} user logs + ${d.security_logs_deleted} security logs removed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally { setCleanupRunning(false); }
  };

  if (loading) return <div className="p-8 text-center"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="admin-site-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Site Settings</h1>
          <p className="text-gray-500 text-sm mt-1">Configure your site name, SEO, branding, and log retention</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="save-site-settings-btn">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-xl border p-6 space-y-6">
        <div className="flex items-center gap-2 text-gray-700 font-medium"><Globe className="w-5 h-5 text-blue-600" /> General</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
            <input value={settings.site_name} onChange={e => setSettings({...settings, site_name: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="GMB Autopilot" data-testid="site-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site URL</label>
            <input value={settings.site_url} onChange={e => setSettings({...settings, site_url: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://gmbautopilot.com" data-testid="site-url-input" />
          </div>
        </div>
      </div>

      {/* SEO Settings */}
      <div className="bg-white rounded-xl border p-6 space-y-6">
        <div className="flex items-center gap-2 text-gray-700 font-medium"><Search className="w-5 h-5 text-green-600" /> SEO Settings (Home Page)</div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
          <input value={settings.meta_title} onChange={e => setSettings({...settings, meta_title: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="GMB Autopilot - AI-Powered Google Business Profile Optimization" data-testid="meta-title-input" />
          <p className="text-xs text-gray-400 mt-1">{(settings.meta_title || '').length}/60 characters recommended</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
          <textarea value={settings.meta_description} onChange={e => setSettings({...settings, meta_description: e.target.value})} rows={3} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Optimize your Google Business Profile with AI-powered tools..." data-testid="meta-desc-input" />
          <p className="text-xs text-gray-400 mt-1">{(settings.meta_description || '').length}/160 characters recommended</p>
        </div>
      </div>

      {/* Branding */}
      <div className="bg-white rounded-xl border p-6 space-y-6">
        <div className="flex items-center gap-2 text-gray-700 font-medium">Branding</div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <input value={settings.logo_url} onChange={e => setSettings({...settings, logo_url: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Footer Text</label>
            <input value={settings.footer_text} onChange={e => setSettings({...settings, footer_text: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="2025 GMB Autopilot" />
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="bg-white rounded-xl border p-6 space-y-6">
        <div className="flex items-center gap-2 text-gray-700 font-medium">Social Links</div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label><input value={settings.social_facebook} onChange={e => setSettings({...settings, social_facebook: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://facebook.com/..." /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Twitter/X</label><input value={settings.social_twitter} onChange={e => setSettings({...settings, social_twitter: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://x.com/..." /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label><input value={settings.social_instagram} onChange={e => setSettings({...settings, social_instagram: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://instagram.com/..." /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label><input value={settings.social_linkedin} onChange={e => setSettings({...settings, social_linkedin: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://linkedin.com/..." /></div>
        </div>
      </div>

      {/* Log Retention Settings */}
      <div className="bg-white rounded-xl border p-6 space-y-6" data-testid="log-retention-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-700 font-medium">
            <Clock className="w-5 h-5 text-orange-500" /> Log Retention Policy
          </div>
          <button
            onClick={handleRetentionSave}
            disabled={retentionSaving}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50"
            data-testid="save-retention-btn"
          >
            <Save className="w-4 h-4" /> {retentionSaving ? 'Saving...' : 'Save Retention'}
          </button>
        </div>

        <p className="text-sm text-gray-500">Configure how long audit logs are retained before automatic cleanup. Security logs are kept longer for compliance.</p>

        {retentionLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User Activity Log Retention</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={7} max={365}
                  value={retention.user_activity_days}
                  onChange={e => setRetention({...retention, user_activity_days: parseInt(e.target.value) || 7})}
                  className="w-24 border rounded-lg px-3 py-2 text-sm text-center"
                  data-testid="user-retention-days-input"
                />
                <span className="text-sm text-gray-500">days</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Min: 7 days. Applies to general user activity logs.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin / Security Log Retention</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={30} max={730}
                  value={retention.admin_security_days}
                  onChange={e => setRetention({...retention, admin_security_days: parseInt(e.target.value) || 30})}
                  className="w-24 border rounded-lg px-3 py-2 text-sm text-center"
                  data-testid="admin-retention-days-input"
                />
                <span className="text-sm text-gray-500">days</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Min: 30 days. Applies to 2FA, login, and admin action logs.</p>
            </div>
          </div>
        )}

        <div className="border-t pt-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Manual Cleanup</p>
            <p className="text-xs text-gray-400">Remove logs older than the retention period immediately.</p>
          </div>
          <button
            onClick={handleRunCleanup}
            disabled={cleanupRunning}
            className="flex items-center gap-2 px-4 py-2 border-2 border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
            data-testid="run-cleanup-btn"
          >
            {cleanupRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            {cleanupRunning ? 'Running...' : 'Run Cleanup Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSiteSettingsPage;
